import { useContext, useMemo, useState } from "react";
import { Download, ExternalLink, X } from "lucide-react";

import AccessDenied from "@/components/common/AccessDenied";
import { ColumnDef, DataTable } from "@/components/common/DataTable";
import MultiSelect from "@/components/common/MultiSelect";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import MetricLegend from "@/components/widgets/MyProviderData/MetricLegend";
import QuarterYearSelector from "@/components/widgets/MyProviderData/QuarterYearSelector";
import QuarterlyPerformanceSection from "@/components/widgets/MyProviderData/QuarterlyPerformanceSection";
import SyncStatus, { formatSyncTime } from "@/components/widgets/MyProviderData/SyncStatus";
import { useQuarterState } from "@/components/widgets/MyProviderData/hooks/useQuarterState";
import { CurrentUserContext } from "@/context/CurrentUserContext";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

import { EprAdminMetric, EprAdminUser, useEprAdminQuarter } from "./useEprAdminQuarter";

// Every user's EPR row and quarterly metrics, filterable. Restricted to roles granted
// 'epr_metrics_admin' in the Connect access-control list. Hiding the page is only cosmetic:
// the API checks the same permission itself and refuses the data without it.

type MetricsFilter = "any" | "meets" | "belowRequirement" | "below" | "notCalculated" | "behindEpr";
type YesNoFilter = "any" | "yes" | "no";

const pct = (v: number | null | undefined) => (v == null ? "" : `${Math.round(v * 100)}%`);
const num = (v: number | null | undefined, digits = 1) => (v == null ? "" : v.toFixed(digits));

const formatMetric = (metric: EprAdminMetric, value: number | null | undefined) => {
  if (value == null) return "N/A";
  return metric.unit === "Days" ? value.toFixed(1) : `${Math.round(value)}%`;
};

// range target judged per item (Oversight): every item within TargetMin - TargetMax
const isRangePerItem = (metric: EprAdminMetric) =>
  metric.targetRule === "AllItems" && metric.targetMin != null && metric.targetMax != null;

const Dot: React.FC<{ meets: boolean | null | undefined }> = ({ meets }) => (
  <span
    className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${
      meets == null ? "bg-gray-400" : meets ? "bg-green-600" : "bg-red-600"
    }`}
  />
);

const optionsOf = (users: EprAdminUser[], pick: (u: EprAdminUser) => string | null) =>
  Array.from(new Set(users.map(pick).filter((v): v is string => !!v)))
    .sort()
    .map((v) => ({ label: v, value: v }));

// label stacked above its control, the same for inputs, selects and multi-selects
const filterField = "flex flex-col gap-1.5";

const csvEscape = (v: unknown) => {
  const s = v == null ? "" : String(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const EprMetricsAdminPage = () => {
  useDocumentTitle("EPR & Metrics");
  const { accessControl } = useContext(CurrentUserContext);
  const canRead = accessControl.canRead("epr_metrics_admin");
  // Provider Data only honors ?username= for mpd_impersonation, so the link is offered with it.
  const canOpenMpd = accessControl.canRead("mpd_impersonation");
  // CSV export only for roles with Write = Yes on epr_metrics_admin (HR and named leads; Tarah 2026-10-20)
  const canExport = accessControl.canWrite("epr_metrics_admin");

  const { year, quarter, years, handleQuarterYearChange } = useQuarterState();
  const { data, loading, error } = useEprAdminQuarter(year, quarter, canRead);

  // Filters
  const [search, setSearch] = useState("");
  const [roles, setRoles] = useState<string[]>([]);
  const [regions, setRegions] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [metricsFilter, setMetricsFilter] = useState<MetricsFilter>("any");
  const [parachute, setParachute] = useState<YesNoFilter>("any");
  const [pctMin, setPctMin] = useState("");
  const [pctMax, setPctMax] = useState("");

  const [selected, setSelected] = useState<EprAdminUser | null>(null);

  const users = useMemo(() => data?.users ?? [], [data]);
  const metrics = useMemo(
    () => (data?.metrics ?? []).filter((m) => !m.isComingSoon).sort((a, b) => a.sortOrder - b.sortOrder),
    [data]
  );

  const roleOptions = useMemo(() => optionsOf(users, (u) => u.role), [users]);
  const regionOptions = useMemo(() => optionsOf(users, (u) => u.region), [users]);
  const statusOptions = useMemo(() => optionsOf(users, (u) => u.status), [users]);

  const filtered = useMemo(() => {
    const text = search.trim().toLowerCase();
    const min = pctMin === "" ? null : Number(pctMin) / 100;
    const max = pctMax === "" ? null : Number(pctMax) / 100;

    return users.filter((u) => {
      if (text && !`${u.fullName ?? ""} ${u.username}`.toLowerCase().includes(text)) return false;
      if (roles.length && !roles.includes(u.role ?? "")) return false;
      if (regions.length && !regions.includes(u.region ?? "")) return false;
      if (statuses.length && !statuses.includes(u.status ?? "")) return false;
      if (parachute !== "any" && u.parachute !== (parachute === "yes")) return false;
      if (min != null && (u.percentageComplete ?? -1) < min) return false;
      if (max != null && (u.percentageComplete ?? Number.MAX_VALUE) > max) return false;

      switch (metricsFilter) {
        case "meets":
          return u.meetsRequirement === true;
        case "belowRequirement":
          return u.meetsRequirement === false;
        case "below":
          return u.metricsApplicable != null && (u.metricsMet ?? 0) < u.metricsApplicable;
        case "notCalculated":
          return u.metricsTotal == null;
        case "behindEpr":
          return u.metricsBehindEpr;
        default:
          return true;
      }
    });
  }, [users, search, roles, regions, statuses, parachute, pctMin, pctMax, metricsFilter]);

  const filtersActive =
    !!search || roles.length > 0 || regions.length > 0 || statuses.length > 0 ||
    metricsFilter !== "any" || parachute !== "any" || pctMin !== "" || pctMax !== "";

  const clearFilters = () => {
    setSearch("");
    setRoles([]);
    setRegions([]);
    setStatuses([]);
    setMetricsFilter("any");
    setParachute("any");
    setPctMin("");
    setPctMax("");
  };

  const columns: ColumnDef<EprAdminUser>[] = useMemo(
    () => [
      {
        key: "fullName",
        header: "Name",
        sortable: true,
        sticky: true,
        sortValue: (u) => (u.fullName ?? u.username).toLowerCase(),
        render: (u) => (
          <span className="inline-flex items-center gap-2">
            <button
              type="button"
              className="text-left text-blue-700 hover:underline dark:text-blue-300"
              onClick={() => setSelected(u)}
              title="Open this person's Quarterly Performance"
            >
              {u.fullName ?? u.username}
            </button>
            {canOpenMpd && (
              <a
                href={`#/provider-data?username=${encodeURIComponent(u.username)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground"
                title="Open this person's Provider Data in a new tab"
                aria-label={`Open ${u.fullName ?? u.username}'s Provider Data in a new tab`}
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            )}
          </span>
        ),
      },
      { key: "role", header: "Role", sortable: true },
      { key: "region", header: "Region", sortable: true },
      { key: "status", header: "Status", sortable: true },
      {
        key: "percentageComplete",
        header: "% Complete",
        sortable: true,
        sortValue: (u) => u.percentageComplete ?? -1,
        render: (u) => pct(u.percentageComplete),
      },
      {
        key: "totalHours",
        header: "Total / Prorated Hrs",
        sortable: true,
        sortValue: (u) => u.totalHours ?? -1,
        render: (u) => `${num(u.totalHours)} / ${num(u.proratedHoursRequired)}`,
      },
      {
        key: "averageHoursPerWeek",
        header: "Avg Hrs/Wk",
        sortable: true,
        sortValue: (u) => u.averageHoursPerWeek ?? -1,
        render: (u) => num(u.averageHoursPerWeek),
      },
      {
        key: "parachute",
        header: "Parachute",
        sortable: true,
        sortValue: (u) => (u.parachute ? 1 : 0),
        render: (u) =>
          u.parachute ? (
            <Badge variant="secondary" title={u.parachuteReason ?? undefined}>
              {u.autoParachuted ? "Auto" : u.parachuteReason || "Yes"}
            </Badge>
          ) : (
            ""
          ),
      },
      {
        key: "quarterlyBonusEarned",
        header: "Bonus Earned",
        sortable: true,
        sortValue: (u) => u.quarterlyBonusEarned ?? -1,
        render: (u) =>
          u.quarterlyBonusEarned == null
            ? ""
            : `$${Math.round(u.quarterlyBonusEarned).toLocaleString()}${u.bonusPaid ? " (paid)" : ""}`,
      },
      {
        // reweighted score: met / applicable, N/A metrics left out (HR 2026-10-15)
        key: "score",
        header: "Score",
        sortable: true,
        sortValue: (u) => u.score ?? -1,
        render: (u) =>
          u.metricsTotal == null ? (
            <span className="text-muted-foreground">Not calculated</span>
          ) : (
            <span
              className="inline-flex items-center gap-2"
              title={u.requiredScore != null ? `${u.requiredScore}% required` : undefined}
            >
              <Dot meets={u.meetsRequirement} />
              {u.score != null ? `${Number(u.score).toFixed(0)}%` : "N/A"}
              <span className="text-muted-foreground">
                ({u.metricsMet} of {u.metricsApplicable})
              </span>
            </span>
          ),
      },
      ...metrics.map<ColumnDef<EprAdminUser>>((m) => ({
        key: `metric_${m.key}`,
        header: m.name,
        sortable: true,
        // a range target judged per item (Oversight) shows only "3 of 10" (PM 2026-10-22, option 3),
        // so it sorts by the share of items within range
        sortValue: (u) => {
          const v = u.metricValues[m.key];
          if (isRangePerItem(m) && v?.value != null && (v.itemCount ?? 0) > 0)
            return (v.itemsMeetingTarget ?? 0) / (v.itemCount ?? 1);
          return v?.value ?? -1;
        },
        render: (u) => {
          const v = u.metricValues[m.key];
          if (!v) return <span className="text-muted-foreground">—</span>;
          // per-item metrics (Oversight, BIP): the overall % can be in range while most items are not
          const perItem = m.targetRule === "AllItems" && v.value != null && (v.itemCount ?? 0) > 1;
          const countOnly = perItem && isRangePerItem(m);
          return (
            <span
              className="inline-flex items-center gap-2 whitespace-nowrap"
              title={perItem ? `${v.itemsMeetingTarget ?? 0} of ${v.itemCount} within ${m.targetLabel}` : undefined}
            >
              <Dot meets={v.meetsTarget} />
              {countOnly ? (
                <span>{v.itemsMeetingTarget ?? 0} of {v.itemCount}</span>
              ) : (
                <span className={v.value == null ? "text-muted-foreground" : ""}>{formatMetric(m, v.value)}</span>
              )}
              {perItem && !countOnly && (
                <span className="text-muted-foreground">
                  ({v.itemsMeetingTarget ?? 0}/{v.itemCount})
                </span>
              )}
            </span>
          );
        },
      })),
      {
        // one "last updated" time, as on MyProviderData; marked when the metrics are older than it
        key: "lastUpdated",
        header: "Updated",
        sortable: true,
        sortValue: (u) => u.eprSyncedAt ?? u.metricsCalculatedAt ?? "",
        render: (u) => (
          <span
            className="inline-flex items-center gap-2 whitespace-nowrap"
            title={
              u.metricsBehindEpr
                ? `Metrics from ${formatSyncTime(u.metricsCalculatedAt)}: entries since then are not counted yet`
                : undefined
            }
          >
            {u.metricsBehindEpr && (
              <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-amber-500" />
            )}
            {formatSyncTime(u.eprSyncedAt ?? u.metricsCalculatedAt) ?? (
              <span className="text-muted-foreground">—</span>
            )}
          </span>
        ),
      },
    ],
    [metrics, canOpenMpd]
  );

  const exportCsv = () => {
    const header = [
      "Username", "Name", "Role", "Region", "Status", "PercentageComplete", "TotalHours",
      "ProratedHoursRequired", "HoursRequired", "HoursBilled", "AverageHoursPerWeek", "Parachute",
      "ParachuteReason", "QuarterlyBonusEarned", "BonusPaid", "Score", "MeetsRequirement", "MetricsMet", "MetricsApplicable",
      ...metrics.flatMap((m) =>
        m.targetRule === "AllItems"
          ? [m.name, `${m.name} Meets`, `${m.name} Items Met`, `${m.name} Items`]
          : [m.name, `${m.name} Meets`]
      ),
      "EprSyncedAt", "MetricsCalculatedAt", "MetricsBehindEpr",
    ];
    const lines = filtered.map((u) =>
      [
        u.username, u.fullName, u.role, u.region, u.status, u.percentageComplete, u.totalHours,
        u.proratedHoursRequired, u.hoursRequired, u.hoursBilled, u.averageHoursPerWeek, u.parachute,
        u.parachuteReason, u.quarterlyBonusEarned, u.bonusPaid, u.score, u.meetsRequirement, u.metricsMet, u.metricsApplicable,
        ...metrics.flatMap((m) => {
          const v = u.metricValues[m.key];
          return m.targetRule === "AllItems"
            ? [v?.value, v?.meetsTarget, v?.itemsMeetingTarget, v?.itemCount]
            : [v?.value, v?.meetsTarget];
        }),
        u.eprSyncedAt, u.metricsCalculatedAt, u.metricsBehindEpr,
      ].map(csvEscape).join(",")
    );
    const blob = new Blob([[header.join(","), ...lines].join("\r\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `EPR_Metrics_${year}Q${quarter}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!canRead) {
    return <AccessDenied />;
  }

  return (
    // On wide screens the page is exactly the window below the 68px fixed header, so the table
    // scrolls inside it and its bottom pager stays on screen; lg:pb-8 lifts the table clear of
    // the chat launcher pinned to the bottom-left corner. Narrow screens, where the filters stack,
    // keep the page scrolling and the table's own 80vh cap.
    <div className="flex flex-col gap-4 p-4 lg:h-[calc(100vh-68px)] lg:pb-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">EPR &amp; Metrics</h1>
          <div className="text-sm text-muted-foreground">
            {data ? `${filtered.length} of ${data.users.length} people` : " "}
          </div>
          {data?.sync && (
            <SyncStatus
              className="mt-1"
              eprSyncedAt={data.sync.lastEprSyncAt}
              metricsCalculatedAt={data.sync.lastMetricsCalculatedAt}
              metricsBehindEpr={data.sync.metricsBehindEpr}
              lastRunStatus={data.sync.lastMetricsRunStatus}
              lastRunAt={data.sync.lastMetricsRunFinishedAt ?? data.sync.lastMetricsRunStartedAt}
            />
          )}
        </div>
        <div className="flex items-end gap-2">
          <QuarterYearSelector
            year={year}
            quarter={quarter}
            years={years}
            onChange={handleQuarterYearChange}
            className="mb-0"
          />
          {canExport && (
            <Button variant="outline" onClick={exportCsv} disabled={!filtered.length}>
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
          )}
        </div>
      </div>

      {/* What each metric means - text from EPR_MetricDefinition.Explanation */}
      {data && <MetricLegend metrics={data.metrics} />}

      {/* Filters */}
      <div className="grid grid-cols-1 gap-3 rounded-md border p-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
        <div className={`${filterField} xl:col-span-2`}>
          <Label>Name</Label>
          <Input placeholder="Search name or login" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className={filterField}>
          <Label>Role</Label>
          <MultiSelect options={roleOptions} value={roles} onChange={setRoles} placeholder="All roles" className="w-full min-h-10" />
        </div>
        <div className={filterField}>
          <Label>Region</Label>
          <MultiSelect options={regionOptions} value={regions} onChange={setRegions} placeholder="All regions" className="w-full min-h-10" />
        </div>
        <div className={filterField}>
          <Label>Status</Label>
          <MultiSelect options={statusOptions} value={statuses} onChange={setStatuses} placeholder="All" className="w-full min-h-10" />
        </div>
        <div className={filterField}>
          <Label>Metrics</Label>
          <Select value={metricsFilter} onValueChange={(v) => setMetricsFilter(v as MetricsFilter)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any</SelectItem>
              <SelectItem value="meets">Meets requirement</SelectItem>
              <SelectItem value="belowRequirement">Below requirement</SelectItem>
              <SelectItem value="below">Any metric below target</SelectItem>
              <SelectItem value="notCalculated">Not calculated</SelectItem>
              <SelectItem value="behindEpr">Metrics behind EPR</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className={filterField}>
          <Label>Parachute</Label>
          <Select value={parachute} onValueChange={(v) => setParachute(v as YesNoFilter)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any</SelectItem>
              <SelectItem value="yes">Yes</SelectItem>
              <SelectItem value="no">No</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className={filterField}>
          <Label>% Complete</Label>
          <div className="flex items-center gap-1">
            <Input type="number" placeholder="min" value={pctMin} onChange={(e) => setPctMin(e.target.value)} />
            <span>–</span>
            <Input type="number" placeholder="max" value={pctMax} onChange={(e) => setPctMax(e.target.value)} />
          </div>
        </div>
        {filtersActive && (
          <div className="flex items-end">
            <Button variant="ghost" onClick={clearFilters}>
              <X className="mr-1 h-4 w-4" />
              Clear filters
            </Button>
          </div>
        )}
      </div>

      <DataTable data={filtered} columns={columns} loading={loading} error={error} pageSize={100} />

      {/* One person's Quarterly Performance */}
      <Sheet open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-5xl">
          <SheetHeader>
            <SheetTitle>
              {selected?.fullName ?? selected?.username}
              {selected?.role ? ` · ${selected.role}` : ""}
              {selected?.region ? ` · ${selected.region}` : ""}
            </SheetTitle>
          </SheetHeader>
          {selected && (
            <div className="mt-4 grid grid-cols-12 gap-4">
              <QuarterlyPerformanceSection
                username={selected.username}
                year={year}
                quarter={quarter}
                display={{ QuarterlyPerformance: true }}
              />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default EprMetricsAdminPage;
